import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { RenewalReminderState } from "@/hooks/subscription/useRenewalReminder";
import { RenewalShareActions } from "./RenewalShareActions";

const CONTINUE_DELAY_SECONDS = 3;

interface RenewalFinalDayProps {
  reminder: RenewalReminderState;
}

export function RenewalFinalDay({ reminder }: RenewalFinalDayProps) {
  const navigate = useNavigate();
  const {
    audience,
    expiryLabel,
    finalDayEndLabel,
    billedThroughCenterName,
    isFinalOpen,
  } = reminder;
  const [secondsLeft, setSecondsLeft] = useState(CONTINUE_DELAY_SECONDS);

  useEffect(() => {
    if (!isFinalOpen) return;
    setSecondsLeft(CONTINUE_DELAY_SECONDS);
    const id = window.setInterval(() => {
      setSecondsLeft((seconds) => {
        if (seconds <= 1) window.clearInterval(id);
        return Math.max(seconds - 1, 0);
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [isFinalOpen]);

  const canContinue = secondsLeft === 0;
  const continueLabel = canContinue
    ? "Continue to dashboard"
    : `Continue in ${secondsLeft}s`;

  const handleContinue = () => {
    if (canContinue) reminder.closeFinalDay();
  };

  const handleRenew = () => {
    reminder.closeFinalDay();
    navigate("/settings?section=subscription");
  };

  const action =
    audience === "payer"
      ? "Renew now to keep uninterrupted access."
      : audience === "branch"
        ? `Let ${billedThroughCenterName || "your main branch"} know now.`
        : "Let the right person know now.";

  return (
    <Dialog open={isFinalOpen} onOpenChange={(open) => !open && handleContinue()}>
      <DialogContent
        className="flex h-[100dvh] max-h-none w-screen max-w-none gap-0 overflow-y-auto border-0 p-0 sm:rounded-none [&>button]:hidden"
        onPointerDownOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (!canContinue) event.preventDefault();
        }}
      >
        <div className="relative hidden w-2/5 shrink-0 items-center justify-center overflow-hidden bg-primary md:flex">
          <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-white/10" />
          <div className="absolute -bottom-32 -right-20 h-96 w-96 rounded-full bg-black/10" />
          <div className="relative w-56 overflow-hidden rounded-3xl bg-card shadow-2xl">
            <div className="h-12 bg-secondary" />
            <div className="space-y-1 px-4 py-6 text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Subscription ends
              </p>
              <p className="text-3xl font-black text-foreground">{expiryLabel}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-center gap-6 px-6 py-10 sm:px-12 lg:px-20">
          <p className="text-xs font-extrabold uppercase tracking-widest text-destructive">
            Last day on your FirstRep portal subscription
          </p>
          <div className="space-y-4">
            <DialogTitle className="text-3xl font-black leading-tight sm:text-5xl">
              Ends {finalDayEndLabel}. {action}
            </DialogTitle>
            <DialogDescription className="max-w-xl text-base sm:text-lg">
              After that, member records, payments and reports lock until the
              subscription is renewed.
            </DialogDescription>
          </div>

          <div className="w-full max-w-md space-y-2">
            {audience === "payer" ? (
              <>
                <Button size="lg" className="w-full font-semibold" onClick={handleRenew}>
                  Renew now
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={!canContinue}
                  onClick={handleContinue}
                >
                  {continueLabel}
                </Button>
              </>
            ) : (
              <RenewalShareActions
                message={reminder.shareMessage}
                onDone={handleContinue}
                doneLabel={continueLabel}
                isDoneDisabled={!canContinue}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
